import { useState, useEffect, useCallback } from "react";
import { API } from "../contexts/AuthContext";

/**
 * Hook to cache a trip's API response for offline access.
 * Works directly with the Cache Storage API (same store as the SW).
 */
export function useOfflineCache(tripId) {
  const [cached, setCached] = useState(false);
  const [caching, setCaching] = useState(false);

  // Check if this trip is already in any SW cache
  const checkCached = useCallback(async () => {
    if (!("caches" in window) || !tripId) return;
    const url = `${API}/trips/${tripId}`;
    const match = await window.caches.match(url);
    setCached(!!match);
  }, [tripId]);

  useEffect(() => { checkCached(); }, [checkCached]);

  // Fetch the trip data (+ its roadmap) and put it in the SW API cache
  const cacheTrip = useCallback(async () => {
    if (!("caches" in window) || !tripId || caching) return false;
    setCaching(true);
    try {
      const tripUrl = `${API}/trips/${tripId}`;
      const roadmapUrl = `${API}/trips/${tripId}/roadmap`;

      // Find the versioned API cache (robust to SW version bumps)
      const keys = await window.caches.keys();
      const apiCacheName = keys.find((k) => k.includes("-api")) || "voyage-r-v6-api";
      const cache = await window.caches.open(apiCacheName);

      const response = await fetch(tripUrl);
      if (response.ok) {
        await cache.put(tripUrl, response.clone());
        setCached(true);
      }

      // Best-effort : met aussi en cache la Feuille de Route (ignore les échecs)
      try {
        const rm = await fetch(roadmapUrl);
        if (rm.ok) await cache.put(roadmapUrl, rm.clone());
      } catch { /* roadmap absente — ignorer */ }

      return response.ok;
    } catch {
      return false;
    } finally {
      setCaching(false);
    }
  }, [tripId, caching]);

  return { cached, caching, cacheTrip, recheckCache: checkCached };
}
